<template>
  <v-dialog
    :model-value="modelValue"
    max-width="640"
    @update:model-value="close"
  >
    <v-card>
      <v-card-title class="text-h6">
        Stauaufschlag für {{ plzList.length }}
        {{ plzList.length === 1 ? "PLZ-Gebiet" : "PLZ-Gebiete" }}
      </v-card-title>

      <v-card-subtitle class="pb-2">
        {{ plzPreview }}
      </v-card-subtitle>

      <v-card-text>
        <!-- Der Aufschlag ersetzt bestehende Werte der Auswahl, er wird nicht
             dazugerechnet. Das muss der Anwender wissen, bevor er speichert. -->
        <v-alert
          v-if="hasExistingValues"
          type="info"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          Für einen Teil der Auswahl sind bereits Aufschläge hinterlegt. Die
          Eingabe <strong>ersetzt</strong> diese Werte.
        </v-alert>

        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model.number="pct"
              type="number"
              min="0"
              max="999"
              step="1"
              label="Prozentualer Aufschlag"
              suffix="%"
              density="compact"
              :error-messages="pctError"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.number="minutes"
              type="number"
              min="0"
              max="9999"
              step="1"
              label="Absoluter Aufschlag"
              suffix="min"
              density="compact"
              :error-messages="minutesError"
            ></v-text-field>
          </v-col>
        </v-row>

        <p class="text-caption text-medium-emphasis mt-n2 mb-4">
          Beides ist kombinierbar: Zeit × (1 + Prozent/100) + Minuten. Beide
          Felder auf 0 entfernt den Aufschlag wieder.
        </p>

        <!-- Vorbelegt sind die straßengebundenen Verkehrsmittel; nur die sind
             überhaupt stauanfällig. Wer abweichen will, klappt die Liste auf. -->
        <v-expansion-panels variant="accordion" class="mb-2">
          <v-expansion-panel>
            <v-expansion-panel-title>
              Verkehrsmittel ({{ selectedTransports.length }} ausgewählt)
              <template #actions>
                <v-icon icon="mdi-chevron-down"></v-icon>
              </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="d-flex justify-end mb-2">
                <v-btn size="x-small" variant="text" @click="selectRoadBound">
                  Nur straßengebundene
                </v-btn>
                <v-btn size="x-small" variant="text" @click="selectAll">
                  Alle
                </v-btn>
                <v-btn size="x-small" variant="text" @click="selectNone">
                  Keine
                </v-btn>
              </div>
              <v-row dense>
                <v-col
                  v-for="transport in transports"
                  :key="transport.transport_type"
                  cols="6"
                >
                  <v-checkbox
                    v-model="selectedTransports"
                    :value="transport.transport_type"
                    :label="labelFor(transport.transport_type)"
                    density="compact"
                    hide-details
                  >
                    <template #label>
                      {{ labelFor(transport.transport_type) }}
                      <v-chip
                        v-if="transport.road_bound"
                        size="x-small"
                        class="ml-2"
                        color="warning"
                        variant="tonal"
                      >
                        Straße
                      </v-chip>
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-alert
          v-if="selectedTransports.length === 0"
          type="warning"
          variant="tonal"
          density="compact"
        >
          Ohne Verkehrsmittel lässt sich kein Aufschlag speichern.
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-btn variant="text" @click="close">Abbrechen</v-btn>
        <!-- Eigener Weg zum Entfernen. Fachlich ist "kein Aufschlag" die
             Abwesenheit einer Regel; ohne diese Schaltfläche müsste man dafür
             zweimal 0 eintippen, was niemand von selbst findet. -->
        <v-btn
          color="error"
          variant="text"
          :disabled="plzList.length === 0 || !hasExistingValues"
          :loading="saving"
          prepend-icon="mdi-delete-sweep"
          @click="reset"
        >
          Aufschläge entfernen
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!canSave"
          :loading="saving"
          @click="save"
        >
          Für {{ plzList.length }}
          {{ plzList.length === 1 ? "Gebiet" : "Gebiete" }} übernehmen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import { transportLabel } from "../config/transportLabels";
import { buildSurchargeValues } from "../utils/plzSelection";
import type { PlzSurchargeTransport } from "../store/plzSurchargeStore";

export default defineComponent({
  name: "PlzSurchargeDialog",

  props: {
    modelValue: { type: Boolean, default: false },
    /** Postleitzahlen, für die der Wert gelten soll. */
    plzList: { type: Array as PropType<string[]>, default: () => [] },
    /** Zulässige Verkehrsmittel, wie das Backend sie meldet. */
    transports: {
      type: Array as PropType<PlzSurchargeTransport[]>,
      default: () => [],
    },
    /** Bereits gepflegte PLZ - für den Hinweis auf das Überschreiben. */
    configuredPlz: { type: Array as PropType<string[]>, default: () => [] },
    saving: { type: Boolean, default: false },
  },

  emits: ["update:modelValue", "save"],

  data() {
    return {
      pct: 0 as number,
      minutes: 0 as number,
      selectedTransports: [] as string[],
    };
  },

  computed: {
    /** Kurzfassung der Auswahl; bei vielen Gebieten nur die ersten. */
    plzPreview(): string {
      if (this.plzList.length === 0) return "Keine Auswahl";
      if (this.plzList.length <= 8) return this.plzList.join(", ");
      return `${this.plzList.slice(0, 8).join(", ")} … (+${
        this.plzList.length - 8
      } weitere)`;
    },
    hasExistingValues(): boolean {
      return this.plzList.some((plz) => this.configuredPlz.includes(plz));
    },
    pctError(): string[] {
      if (this.pct === null || this.pct === undefined) return [];
      if (this.pct < 0) return ["Der Aufschlag darf nicht negativ sein."];
      if (this.pct > 999.99) return ["Höchstens 999,99 %."];
      return [];
    },
    minutesError(): string[] {
      if (this.minutes === null || this.minutes === undefined) return [];
      if (this.minutes < 0) return ["Der Aufschlag darf nicht negativ sein."];
      if (this.minutes > 9999.99) return ["Höchstens 9999,99 Minuten."];
      return [];
    },
    canSave(): boolean {
      return (
        this.plzList.length > 0 &&
        this.selectedTransports.length > 0 &&
        this.pctError.length === 0 &&
        this.minutesError.length === 0
      );
    },
  },

  watch: {
    // Beim Öffnen die straßengebundenen Verkehrsmittel vorbelegen und die
    // Eingaben zurücksetzen, damit kein Wert aus einer früheren Auswahl
    // stehen bleibt.
    modelValue(open: boolean) {
      if (!open) return;
      this.pct = 0;
      this.minutes = 0;
      this.selectRoadBound();
    },
  },

  methods: {
    labelFor(transportType: string): string {
      return transportLabel(transportType);
    },
    selectRoadBound() {
      this.selectedTransports = this.transports
        .filter((transport) => transport.road_bound)
        .map((transport) => transport.transport_type);
    },
    selectAll() {
      this.selectedTransports = this.transports.map(
        (transport) => transport.transport_type,
      );
    },
    selectNone() {
      this.selectedTransports = [];
    },
    close() {
      this.$emit("update:modelValue", false);
    },
    /**
     * Aufschläge der Auswahl entfernen.
     *
     * Geht über denselben Speicherweg: 0 in beiden Feldern löscht die Regel.
     * Damit wirklich alles verschwindet, werden ALLE Verkehrsmittel
     * zurückgesetzt, nicht nur die gerade angehakten - sonst bliebe ein
     * Aufschlag stehen, den der Anwender im Dialog gar nicht sieht.
     */
    reset() {
      if (this.plzList.length === 0) return;
      this.$emit("save", {
        plzList: [...this.plzList],
        values: buildSurchargeValues(
          this.transports.map((transport) => transport.transport_type),
          0,
          0,
        ),
      });
    },
    save() {
      if (!this.canSave) return;
      this.$emit("save", {
        plzList: [...this.plzList],
        values: buildSurchargeValues(
          this.selectedTransports,
          this.pct,
          this.minutes,
        ),
      });
    },
  },
});
</script>
